import { useCallback, useEffect, useRef } from 'react';
import { makeCancelable } from 'utils/promises';
import history from 'common/history';
import { useSelector } from 'react-redux';
import { selectCurrentFileMetData, selectQuery } from 'common/selectors';
import { useSendMessage } from '../redux/sendMessage';
import { playerActions } from './enums';
import { useLeaveAGroup } from '../redux/leaveAGroup';
import { useStartPresenting } from '../redux/startPresenting';

export const useSendMessages = ({ present, startPlayerPresenting, fileId }: any) => {
  const { sendMessage: sendMessageApi } = useSendMessage();
  const { leaveAGroup } = useLeaveAGroup();
  const { startPresenting } = useStartPresenting();

  const queryParam = useSelector(selectQuery);
  const currentFileMetaData = useSelector(selectCurrentFileMetData);

  // Cancelable promises refs
  const startPresentingSafeRef = useRef<any>(null);
  const leaveGroupSafeRef = useRef<any>(null);

  const isPresenting = !!present;

  // Helper: wrap message
  const wrapMessage = useCallback(
    (message: any) => ({
      message: {
        ...message,
        target: 'present',
        room: present, // 'private' or 'public'
      },
    }),
    [present],
  );

  // Helper: send message
  const sendMessage = useCallback(
    (message: any) => {
      sendMessageApi(wrapMessage(message));
    },
    [sendMessageApi, wrapMessage],
  );

  // Helper: get full message
  const getFullMessage = useCallback(
    (action = playerActions.PAUSE, time = 0) => ({
      fileId,
      fileType: 'mp4',
      videoAction: action,
      videoTime: time,
    }),
    [fileId],
  );

  // Helper: send full message
  const sendFullMessage = useCallback(
    (action = playerActions.PAUSE, time = 0) => {
      sendMessage(wrapMessage(getFullMessage(action, time)));
    },
    [sendMessage, wrapMessage, getFullMessage],
  );

  // Present/leave logic
  const startStopPresentHandler = useCallback(
    (mode?: string, forcePresent?: boolean) => {
      const containsFolderId = Object.keys(queryParam).includes('folderId');
      const folderIdParamStr = `folderId=${queryParam.folderId}`;
      if (
        isPresenting &&
        (!forcePresent || (present === 'public' && currentFileMetaData?.private))
      ) {
        leaveGroupSafeRef.current = makeCancelable(
          new Promise<void>((r) => leaveAGroup('present').then(() => r())),
        );
        leaveGroupSafeRef.current.promise.then(() => {
          history.push(
            history.location.pathname.replace(/\?present.*/, ``) +
              `${containsFolderId ? `?${folderIdParamStr}` : ''}`,
          );
        });
      } else {
        startPresentingSafeRef.current = makeCancelable(
          new Promise<void>((r) =>
            startPresenting({
              hearingRoomMode: mode,
              message: getFullMessage(),
            }).then(() => r()),
          ),
        );
        startPresentingSafeRef.current.promise.then(() => {
          history.push(
            history.location.pathname.replace(/\?present.*/, ``) +
              `?${containsFolderId ? `${folderIdParamStr}&` : ''}present=${mode}`,
          );
          startPlayerPresenting();
        });
      }
    },
    [
      currentFileMetaData?.private,
      getFullMessage,
      isPresenting,
      leaveAGroup,
      present,
      queryParam,
      startPlayerPresenting,
      startPresenting,
    ],
  );

  useEffect(() => {
    return () => {
      startPresentingSafeRef.current?.cancel();
      leaveGroupSafeRef.current?.cancel();
    };
  }, []);

  return {
    sendFullMessage,
    startStopPresentHandler,
  };
};
